import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Copy,
  Cpu,
  Flag,
  HelpCircle,
  Maximize2,
  MessageSquare,
  MinusCircle,
  Pencil,
  Search,
  Trash,
  XCircle,
} from "lucide-react";
import {
  memo,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  MFPersonIdentity,
  MFReviewAccount,
} from "state/members/accessReviews";
import { accessKindLabel, isIndirectAccount } from "state/accessPaths";
import PloyPopover from "components/PloyPopover";

import { Select } from "@chakra-ui/react";
import { format } from "date-fns";
import { Calendar, Clock, Shield } from "lucide-react";
import {
  AppLogo,
  Avatar,
  Button,
  Chip,
  CopyableValue,
  type ButtonTone,
  type CopyableValueFont,
} from "components/ds";
import AccessPathBreadcrumbs from "./AccessPathBreadcrumbs";
import CustomCheckbox from "./Checkbox";
import {
  frozenLeftSx,
  getGridTemplateColumns,
  STICKY_ACTIONS_SX,
} from "./VirtualizedGroup";
import QueryChip from "../components/QueryChip";
import {
  accountCellName,
  activityBand,
  activityChipLabel,
  activityUnavailableLabel,
  canCorrectEntitlements,
  entitlementChipParts,
  entitlementTooltipLabel,
  fitChips,
  FROZEN_LEFT_OFFSETS,
  FROZEN_REVIEW_COLUMN,
  identityDisplayValue,
  identityProvider,
  isAccountDecisionFrozen,
  isNhiAccount,
  orderedPersonIdentities,
  peerCompactLabel,
  personIdentityLabel,
  RAISE_QUERY_ACTION,
  type PersonRowExtras,
  resolveLastAccessed,
  rowActionButtonCount,
  rowActionSequence,
  removalActionTooltip,
  previousReviewOf,
  previousReviewMeta,
  previousOutcomeIconKind,
  previousOutcomeShort,
  type PreviousOutcomeIconKind,
  type IdentityProviderKey,
} from "../reviewLogic";
import type { IconType } from "react-icons";
import { FcGoogle } from "react-icons/fc";
import {
  SiAuth0,
  SiGithub,
  SiGitlab,
  SiOkta,
  SiSlack,
} from "react-icons/si";
import { FaMicrosoft, FaAws } from "react-icons/fa";

// Helper function to check if an account is locked: its decision started a
// removal that has happened, so it can no longer be changed. Delegates to the
// shared `isAccountDecisionFrozen` source of truth (PLO-5749).
export const isAccountLocked = (
  account: MFReviewAccount,
  decisionOnly = false,
  reversibleOutcomes?: readonly string[],
): boolean =>
  isAccountDecisionFrozen(account, { decisionOnly, reversibleOutcomes });

// Helper to check if a column is an entitlement type column
const isEntitlementTypeColumn = (column: string): boolean =>
  column.startsWith("entitlement_type_");

const sentenceCase = (value: string): string => {
  const text = value.replace(/_/g, " ").trim();
  if (!text) return "";
  return `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`;
};

/** Thin wrapper over the DS CopyableValue: keeps this file's existing
 *  `{ value, label }` call signature and its em-dash empty state. */
const ClipboardValue = memo(
  ({
    value,
    label = "Copy value",
    font = "body",
  }: {
    value?: string | null;
    label?: string;
    font?: CopyableValueFont;
  }) => {
    if (!value) {
      return (
        <Text fontSize="xs" color="text.body.tertiary" lineHeight="1">
          -
        </Text>
      );
    }

    return <CopyableValue value={value} font={font} copyLabel={label} />;
  },
);

// Brand visual per identity provider — a small logo tile. Providers not in this
// map (identityProvider → null key) fall back to the generic Shield tile.
const IDENTITY_VISUAL: Record<
  IdentityProviderKey,
  { Icon: IconType; bg: string; color?: string }
> = {
  google: { Icon: FcGoogle, bg: "bg.container" },
  microsoft: { Icon: FaMicrosoft, bg: "bg.container", color: "#0078D4" },
  okta: { Icon: SiOkta, bg: "bg.container", color: "#007DC1" },
  github: { Icon: SiGithub, bg: "#24292e", color: "neutral.0" },
  gitlab: { Icon: SiGitlab, bg: "bg.container", color: "#FC6D26" },
  slack: { Icon: SiSlack, bg: "bg.container", color: "#4A154B" },
  aws: { Icon: FaAws, bg: "bg.container", color: "#FF9900" },
  auth0: { Icon: SiAuth0, bg: "bg.container", color: "#EB5424" },
};

// Provider logo tile (28px, elevLow) — branded when known, generic Shield
// otherwise. Mirrors design.tsx IdentityAvatar.
// A row's resource: its logo (else its integration's mark, else initials), its
// name, and its source identifier to copy.
const ResourceCell = memo(({ account }: { account: MFReviewAccount }) => {
  const { resource } = account;
  const provider = identityProvider({ resource: { type: resource?.integration } });

  return (
    <HStack spacing="sm" align="flex-start" minW={0} w="full">
      {resource?.logoUrl || !provider.key ? (
        <AppLogo name={resource?.name ?? "?"} logoUrl={resource?.logoUrl ?? undefined} size="md" />
      ) : (
        <IdentityAvatar providerKey={provider.key} />
      )}
      <VStack align="flex-start" spacing="2xs" minW={0} flex="1">
        <Text color="text.body.primary" fontSize="sm" fontWeight="600" noOfLines={1} title={resource?.name}>
          {resource?.name ?? "-"}
        </Text>
        {resource?.extId && <ClipboardValue value={resource.extId} label="Copy ID" font="mono" />}
      </VStack>
    </HStack>
  );
});

export const IdentityAvatar = memo(
  ({ providerKey }: { providerKey: IdentityProviderKey | null }) => {
    const visual = providerKey ? IDENTITY_VISUAL[providerKey] : undefined;
    return (
      <Flex
        w="28px"
        h="28px"
        borderRadius="component.md"
        bg={visual?.bg ?? "bg.section"}
        color={visual?.color ?? "text.body.secondary"}
        boxShadow="elevLow"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={visual?.Icon ?? Shield} boxSize={3.5} />
      </Flex>
    );
  },
);

// Read-only list of every identity a grouped person row holds (PLO-5424),
// shown in the identity cell's hover popover. Primary first and marked.
export const PersonIdentitiesList = memo(
  ({
    identities,
    resource,
  }: {
    identities: MFPersonIdentity[];
    resource?: { type?: string | null } | null;
  }) => {
    const ordered = orderedPersonIdentities({ personIdentities: identities });
    return (
      <VStack align="stretch" spacing={0} py={1} minW="260px" maxW="340px">
        <Text
          px={3}
          py={1}
          fontSize="2xs"
          fontWeight="600"
          color="text.body.tertiary"
          textTransform="uppercase"
          letterSpacing="0.04em"
        >
          Identities ({ordered.length})
        </Text>
        {ordered.map((identity) => {
          const label = personIdentityLabel(identity, resource);
          return (
            <HStack key={identity.id} spacing="sm" px={3} py={2} align="center">
              <IdentityAvatar providerKey={label.provider.key} />
              <VStack align="flex-start" spacing="2xs" minW={0} flex="1">
                <HStack spacing="6px" minW={0}>
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="text.body.primary"
                    noOfLines={1}
                  >
                    {label.provider.name}
                    {label.authTypeLabel ? ` · ${label.authTypeLabel}` : ""}
                    {label.mfaEnabled ? " · MFA" : ""}
                  </Text>
                  {label.isPrimary && (
                    <Text
                      fontSize="2xs"
                      fontWeight="600"
                      color="p.700"
                      bg="p.50"
                      px="6px"
                      py="1px"
                      borderRadius="full"
                      whiteSpace="nowrap"
                    >
                      Primary
                    </Text>
                  )}
                </HStack>
                <Text
                  fontSize="xs"
                  color="text.body.tertiary"
                  fontFamily="mono"
                  noOfLines={1}
                >
                  {label.value}
                </Text>
              </VStack>
            </HStack>
          );
        })}
      </VStack>
    );
  },
);

const AccountRow = memo(
  ({
    account,
    isSelected,
    onSelect,
    onActionSelect,
    onEditEntitlements,
    outcomeOptions,
    visibleColumns = [],
    splitEntitlementsByType = false,
    canEditEntitlements = false,
    columnWidths = {},
    onOpenAccount,
    decisionOnly = false,
    reversibleOutcomes,
    entitlementOnly = false,
    freezeAccount = false,
    personGrouped = false,
  }: {
    // Flat rows carry none of the person extras; grouped person rows
    // (PLO-5424) add every identity the person holds so the identity cell can
    // show the primary + an all-identities hover popover.
    account: MFReviewAccount & Partial<PersonRowExtras>;
    isSelected: boolean;
    onSelect: (id: string, checked: boolean, a?: any) => void;
    onActionSelect: (id: string, action: string) => void;
    onEditEntitlements?: (accountId: string) => void;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
    visibleColumns?: string[];
    splitEntitlementsByType?: boolean;
    canEditEntitlements?: boolean;
    // Drag-to-resize column widths (PLO-4818, item 5) — kept in lockstep with
    // the header via the shared getGridTemplateColumns.
    columnWidths?: Record<string, number>;
    // Open the per-account detail drawer (PLO-4818, item 4).
    onOpenAccount?: (accountId: string) => void;
    // Cycle records decisions only — never deprovisions — so rows never lock
    // and reviewers can re-decide (PLO-5083).
    decisionOnly?: boolean;
    // Locking outcomes this cycle configures with no remediation action — this
    // row's outcome being one of them means it never started de-provisioning,
    // so the row must stay editable (PLO-5749).
    reversibleOutcomes?: readonly string[];
    // Entitlement-only cycle (PLO-5731): this row IS one entitlement. Drops the
    // flag-for-adjustment action and renames a removal after the entitlement.
    entitlementOnly?: boolean;
    // Freeze the checkbox and Account cells (PLO-6216) — set by the
    // group only when the scrollport has room for it.
    freezeAccount?: boolean;
    // Grouped by person: the person sits in the group header, so the row's
    // leading cell names the resource the row is about instead.
    personGrouped?: boolean;
  }) => {
    // Check if account is locked from further changes
    const isLocked = isAccountLocked(account, decisionOnly, reversibleOutcomes);

    // An NHI row shows the chip icon + a typed label below the name,
    // matching the dashboard NHI list styling.
    const isNhi = isNhiAccount(account);
    const nhiLabel: Record<string, string> = {
      service_account: "Service account",
      agent_identity: "Agent",
      api_key: "API key",
    };
    // Shared with the table's name sort (PLO-6218), so the order matches.
    const displayName = accountCellName(account);
    const displaySubtitle = isNhi
      ? nhiLabel[account.identity?.type ?? ""] || "Non-human identity"
      : account.member.email || "";
    const hasFinalReview = Boolean(
      account.review && account.review !== "query",
    );
    const rowBg = isSelected ? "bg.section" : "bg.container";
    const pinLeft = (left: number) =>
      freezeAccount ? frozenLeftSx(left, rowBg) : {};
    const canEditThisEntitlement =
      canEditEntitlements &&
      !isLocked &&
      !!onEditEntitlements &&
      canCorrectEntitlements(account);

    const indirect = isIndirectAccount(account);
    const routes = account.accessPaths ?? [];
    const [chainOpen, setChainOpen] = useState(false);

    // Helper function to render column content
    const renderColumn = (columnName: string) => {
      switch (columnName) {
        case "member":
          if (personGrouped) return <ResourceCell account={account} />;
          return (
            <HStack
              spacing="sm"
              align="flex-start"
              role="group"
              w="full"
              minW={0}
            >
              {isNhi ? (
                <Flex
                  w="24px"
                  h="24px"
                  borderRadius="full"
                  bg="p.100"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={Cpu} boxSize="13px" color="p.700" />
                </Flex>
              ) : (
                <Avatar
                  name={displayName}
                  src={account.member.profilePictureUrl || undefined}
                  size="md"
                />
              )}
              <VStack align="flex-start" spacing="2xs" minW={0} flex="1">
                <HStack spacing="6px" minW={0} w="full" align="center">
                  <Text
                    color="text.body.primary"
                    fontFamily="body"
                    fontSize="sm"
                    fontWeight="500"
                    lineHeight="1.2"
                    noOfLines={1}
                    minW={0}
                    flexShrink={1}
                  >
                    {displayName}
                  </Text>
                  {/* Answered-query chip inline next to the name; clicking it
                      opens the account drawer to re-decide (PLO-4803). */}
                  <QueryChip
                    account={account}
                    show="answered"
                    onClick={
                      onOpenAccount
                        ? () => onOpenAccount(account.id)
                        : undefined
                    }
                  />
                </HStack>
                <ClipboardValue
                  value={displaySubtitle}
                  label="Copy account value"
                />
                {/* Department and Job title are independently optional, so this
                    reads whichever exist and only bridges them when both do.
                    It used to render nothing at all unless department was set,
                    which was survivable while Job title had its own column —
                    PLO-5119 removed that column, so this cell is now the only
                    place either field appears. */}
                {(account.member.department || account.member.jobTitle) && (
                  <Text
                    color="text.body.tertiary"
                    fontFamily="body"
                    fontSize="xs"
                    fontWeight="500"
                    lineHeight="1.2"
                    noOfLines={1}
                  >
                    {[account.member.department, account.member.jobTitle]
                      .filter(Boolean)
                      .join(" • ")}
                  </Text>
                )}
                {/* "Queried" (awaiting) chip stays in the meta stack; the
                    answered chip moved inline next to the name above. The
                    stage-1 decision now lives in its own "Previous review"
                    column (PLO-4806), not inline, to reclaim row height. */}
                <QueryChip account={account} show="queried" />
              </VStack>
              {/* hover-revealed expand → opens the account detail drawer
                  (PLO-4818, item 4). */}
              {onOpenAccount && (
                <IconButton
                  aria-label="View account details"
                  icon={<Icon as={Maximize2} boxSize="14px" />}
                  size="xs"
                  variant="ghost"
                  color="text.body.tertiary"
                  ml="auto"
                  mr="sm"
                  alignSelf="center"
                  opacity={0}
                  _groupHover={{ opacity: 1 }}
                  transition="opacity 120ms, background-color 120ms, color 120ms"
                  _hover={{ bg: "neutral.100", color: "text.body.primary" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAccount(account.id);
                  }}
                />
              )}
            </HStack>
          );

        // No "department"/"jobTitle" cases: both are rendered under the
        // member's name in the Account cell above, and were dropped as
        // standalone columns in PLO-5119.

        case "location":
          return (
            <Text
              color="text.body.primary"
              fontFamily="body"
              fontSize="xs"
              fontWeight="500"
              lineHeight="1.2"
            >
              {account.member.location || "-"}
            </Text>
          );

        case "entitlements":
          return (
            <EntitlementsCell
              account={account}
              onEdit={
                canEditThisEntitlement
                  ? () => onEditEntitlements?.(account.id)
                  : undefined
              }
            />
          );

        case "lastUsed":
          return <LastActivityCell account={account} />;

        case "accessGrantedAt":
          return (
            <Box>
              {account.accessGrantedAt ? (
                <HStack spacing={1}>
                  <Icon as={Calendar} boxSize={3.5} color="text.body.tertiary" />
                  <Text
                    color="text.body.tertiary"
                    fontFamily="body"
                    fontSize="sm"
                    fontWeight="500"
                    lineHeight="1.2"
                  >
                    {format(new Date(account.accessGrantedAt), "MMM d, yyyy")}
                  </Text>
                </HStack>
              ) : (
                <Text
                  color="text.body.tertiary"
                  fontFamily="body"
                  fontSize="sm"
                  fontWeight="500"
                  lineHeight="1.2"
                >
                  -
                </Text>
              )}
            </Box>
          );

        case "accessExpiresAt":
          return (
            <Box>
              {account.accessExpiresAt ? (
                <HStack spacing={1}>
                  <Icon as={Calendar} boxSize={3} color="text.body.tertiary" />
                  <Text
                    color="text.body.primary"
                    fontFamily="body"
                    fontSize="xs"
                    fontWeight="500"
                    lineHeight="1.2"
                  >
                    {format(new Date(account.accessExpiresAt), "MMM d, yyyy")}
                  </Text>
                </HStack>
              ) : (
                <Text
                  color={
                    account.isIndefinite ? "success.700" : "text.body.tertiary"
                  }
                  fontFamily="body"
                  fontSize="xs"
                  fontWeight="500"
                  lineHeight="1.2"
                >
                  {account.isIndefinite ? "Indefinite" : "-"}
                </Text>
              )}
            </Box>
          );

        case "identity": {
          if (!account.identity) {
            return (
              <Box minW={0}>
                <Text color="text.body.tertiary" fontSize="xs" fontWeight="500">
                  -
                </Text>
              </Box>
            );
          }
          const provider = identityProvider(account);
          // Grouped person rows (PLO-5424): the cell shows the PRIMARY
          // identity exactly as before, plus an "N identities" indicator when
          // the person holds more; hovering opens a read-only popover listing
          // every identity with the primary marked.
          const personIdentities = account.personIdentities ?? [];
          const hasMultipleIdentities = personIdentities.length > 1;
          const identityContent = (
            <Box minW={0}>
              <HStack spacing="sm" minW={0}>
                <IdentityAvatar providerKey={provider.key} />
                <VStack align="flex-start" spacing="2xs" minW={0}>
                  <Text
                    color="text.body.primary"
                    fontFamily="body"
                    fontSize="xs"
                    fontWeight="600"
                    lineHeight="1.1"
                    noOfLines={1}
                  >
                    {provider.name}
                    {account.identity.mfa_enabled ? " · MFA" : ""}
                  </Text>
                  <ClipboardValue
                    value={identityDisplayValue(account.identity)}
                    label="Copy identity"
                    font="mono"
                  />
                  {hasMultipleIdentities && (
                    <Text
                      fontSize="2xs"
                      fontWeight="600"
                      color="p.700"
                      bg="p.50"
                      px="6px"
                      py="1px"
                      borderRadius="full"
                      whiteSpace="nowrap"
                    >
                      {personIdentities.length} identities
                    </Text>
                  )}
                </VStack>
              </HStack>
            </Box>
          );
          if (!hasMultipleIdentities) return identityContent;
          return (
            <PloyPopover type="hover" button={identityContent}>
              <PersonIdentitiesList
                identities={personIdentities}
                resource={account.resource}
              />
            </PloyPopover>
          );
        }

        case "resource":
          return (
            <Text
              color="text.body.primary"
              fontSize="xs"
              fontWeight="500"
              noOfLines={1}
              title={account.resource?.name}
            >
              {account.resource?.name ?? "-"}
            </Text>
          );

        case "accessKind": {
          if (!indirect) {
            return (
              <Chip tone="default">{accessKindLabel(account.accessKind)}</Chip>
            );
          }
          return (
            <HStack spacing="2xs" minW={0}>
              <Chip tone="azurite" dot="azurite">
                {accessKindLabel(account.accessKind)}
              </Chip>
              {routes.length > 0 && (
                <IconButton
                  aria-label={
                    chainOpen ? "Hide access chain" : "Show access chain"
                  }
                  icon={
                    <Icon
                      as={chainOpen ? ChevronDown : ChevronRight}
                      boxSize="14px"
                    />
                  }
                  size="xs"
                  variant="ghost"
                  color="text.body.tertiary"
                  onClick={(event) => {
                    event.stopPropagation();
                    setChainOpen((open) => !open);
                  }}
                />
              )}
            </HStack>
          );
        }

        case "review":
          return (
            <ReviewChip
              status={account.review}
              outcomeOptions={outcomeOptions}
              flaggedCount={account.flaggedCount}
            />
          );

        case "status":
          return (
            <Box>
              <StatusIndicator
                status={account.review}
                outcomeOptions={outcomeOptions}
              />
            </Box>
          );

        case "previousReview":
          return <PreviousReviewCell account={account} />;

        case "actions":
          return (
            <Box>
              <ActionButtons
                account={account}
                onSelect={onActionSelect}
                outcomeOptions={outcomeOptions}
                decisionOnly={decisionOnly}
                reversibleOutcomes={reversibleOutcomes}
                entitlementOnly={entitlementOnly}
              />
            </Box>
          );

        default:
          // Handle entitlement_type_* columns
          if (columnName.startsWith("entitlement_type_")) {
            const typeName = columnName.replace("entitlement_type_", "");
            // Compute displayed types (excluding "Other") for proper filtering
            const displayedTypes = visibleColumns
              .filter(
                (c) =>
                  c.startsWith("entitlement_type_") &&
                  c !== "entitlement_type_Other",
              )
              .map((c) => c.replace("entitlement_type_", ""));
            return (
              <EntitlementTypeCell
                account={account}
                typeName={typeName}
                displayedTypes={displayedTypes}
                onEdit={
                  canEditThisEntitlement
                    ? () => onEditEntitlements?.(account.id)
                    : undefined
                }
              />
            );
          }
          return (
            <Text fontSize="xs" fontWeight="400" color="text.body.tertiary">
              -
            </Text>
          );
      }
    };

    return (
      <>
        <Box
          display="grid"
          gridTemplateColumns={getGridTemplateColumns(
            visibleColumns,
            columnWidths,
            rowActionButtonCount(outcomeOptions, entitlementOnly),
          )}
          // Left gutter only. A right padding here sits beyond the last (pinned)
          // track, so it would add scroll width past the sticky actions cell and
          // cause a second horizontal scrollbar. The right-edge gutter lives
          // inside the actions cell instead (pr in STICKY_ACTIONS_SX).
          pl={4}
          minHeight="64px"
          height="100%"
          borderBottom="1px solid"
          borderColor="border.subtle"
          alignItems="stretch"
          bg={rowBg}
          position="relative"
          transition="background-color 120ms"
          // `role="group"` stays: it drives the hover-reveal of the row's action
          // controls. The row itself no longer tints on hover — rows aren't
          // clickable, so the tint implied an affordance that isn't there.
          role="group"
          // Reviewed keyline: a full-height azurite hairline flush to the row's
          // left edge (design B1) — 1px, no vertical inset, no radius.
          _before={
            hasFinalReview
              ? {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "1px",
                  bg: "p.500",
                }
              : undefined
          }
        >
          {/* Checkbox column - hidden once the row is decided (any outcome), so a
              bulk/alternate action can't overwrite an explicit decision. */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={2}
            {...pinLeft(FROZEN_LEFT_OFFSETS.checkbox)}
          >
            {!hasFinalReview && (
              <CustomCheckbox
                checked={isSelected}
                onChange={(checked) => onSelect(account.id, checked)}
              />
            )}
          </Box>

          {/* Render dynamic columns */}
          {visibleColumns.map((column, index) => {
            // Find entitlement column indices for styling
            const entitlementIndices = visibleColumns
              .map((col, idx) => (isEntitlementTypeColumn(col) ? idx : -1))
              .filter((idx) => idx !== -1);
            const firstEntitlementIndex = entitlementIndices[0] ?? -1;
            const lastEntitlementIndex =
              entitlementIndices[entitlementIndices.length - 1] ?? -1;

            const isEntitlement = isEntitlementTypeColumn(column);
            const isFirstEntitlement =
              splitEntitlementsByType && index === firstEntitlementIndex;
            const isLastEntitlement =
              splitEntitlementsByType && index === lastEntitlementIndex;
            const isActions = column === "actions";
            const isStatusColumn = column === "review" || column === "status";
            const isEntitlementColumn =
              column === "entitlements" || isEntitlementTypeColumn(column);

            const ENTITLEMENT_BG = "bg.section";
            const ENTITLEMENT_BORDER = "border.default";

            // Once a row has a decision, non-essential cells recede (design B2).
            // The status chip + actions (and the checkbox, rendered
            // outside this map) stay full; entitlement cells only fade once the
            // decision is irreversible (not_required / locked).
            const rowDecided = hasFinalReview || (account.flaggedCount ?? 0) > 0;
            const columnFaded =
              rowDecided &&
              !isStatusColumn &&
              !isActions &&
              (!isEntitlementColumn || isLocked);
            const columnOpacity = columnFaded ? 0.5 : 1;

            // The actions column is pinned to the right edge so it stays visible
            // while wide entitlement data scrolls horizontally underneath it. Its
            // background must track the row state (selected) so scrolled content
            // doesn't show through the pinned cell. No hover tint: rows don't
            // have one either, and on a pinned cell it read as a lit-up block.
            //
            // The Account column is frozen at the left for the same reason, so
            // the reviewer sees whose row they are on (PLO-6216). A frozen cell
            // fades its CONTENT, not itself: opacity on the cell would make its
            // background see-through and show the columns scrolling under it.
            const isMember = column === FROZEN_REVIEW_COLUMN;
            const stickyProps = isActions
              ? {
                  ...STICKY_ACTIONS_SX,
                  zIndex: 1,
                  bg: rowBg,
                }
              : isMember && freezeAccount
                ? {
                    ...pinLeft(FROZEN_LEFT_OFFSETS.member),
                    opacity: 1,
                    sx: { "& > *": { opacity: columnOpacity } },
                  }
                : {};

            return (
              <Box
                key={column}
                bg={
                  splitEntitlementsByType && isEntitlement
                    ? ENTITLEMENT_BG
                    : "transparent"
                }
                borderLeft={
                  splitEntitlementsByType && isEntitlement ? "1px solid" : "none"
                }
                borderRight={isLastEntitlement ? "1px solid" : "none"}
                borderColor={ENTITLEMENT_BORDER}
                // Matches the header cell's pl so columns line up; entitlement
                // sub-columns keep their own wider gutter.
                pl={splitEntitlementsByType && isEntitlement ? 3 : "md"}
                mr={isLastEntitlement ? 3 : 0}
                py={3}
                display="flex"
                alignItems="center"
                opacity={columnOpacity}
                // Keep each cell within its grid track. Without this, nowrap
                // content (e.g. entitlement chips) spills past the cell and widens
                // the row beyond the header, adding a second horizontal scrollbar
                // and letting the row scroll further than the pinned actions cell.
                minWidth={0}
                // Identity opts out of clipping too: its provider tile carries an
                // elevLow shadow that a hidden overflow shears off at the cell
                // edge. Its content truncates on its own, so nothing spills.
                overflow={
                  isActions || column === "identity" ? "visible" : "hidden"
                }
                {...stickyProps}
              >
                {renderColumn(column)}
              </Box>
            );
          })}
        </Box>

        {indirect && chainOpen && routes.length > 0 && (
          <VStack
            align="stretch"
            spacing="md"
            bg="bg.section"
            borderBottom="1px solid"
            borderColor="border.subtle"
            px={4}
            py={3}
          >
            {routes.map((route, index) => (
              <AccessPathBreadcrumbs
                key={`${route.leafAccessId}-${index}`}
                path={route}
              />
            ))}
          </VStack>
        )}
      </>
    );
  },
);

// Dashed-ring glyph for the "not reviewed" status pill
// (design.tsx DashedCircleIcon).
const DashedCircleIcon: IconType = (props) => (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 16 16">
    <circle
      cx="8"
      cy="8"
      r="6.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeDasharray="2.4 2.4"
    />
  </svg>
);

const getReviewChipStyle = (
  status: string | null | undefined,
  flaggedCount = 0,
) => {
  if (!status) {
    if (flaggedCount > 0) {
      return {
        bg: "warning.50",
        borderColor: "warning.200",
        borderStyle: "solid",
        icon: Flag,
        iconColor: "warning.600",
        textColor: "warning.800",
        label: "Entitlements flagged",
      };
    }
    return {
      bg: "transparent",
      borderColor: "border.strong",
      borderStyle: "dashed",
      icon: DashedCircleIcon,
      iconColor: "text.body.tertiary",
      textColor: "text.body.tertiary",
      label: "Not reviewed",
    };
  }
  switch (status) {
    case "required":
    case "keep":
    case "compliant":
    case "appropriate":
      return {
        bg: "success.50",
        borderColor: "success.200",
        borderStyle: "solid",
        icon: CheckCircle,
        iconColor: "success.700",
        textColor: "success.800",
        label: undefined,
      };
    case "adjust_entitlements":
    case "modify":
    case "query":
      return {
        bg: "warning.50",
        borderColor: "warning.200",
        borderStyle: "solid",
        icon: AlertTriangle,
        iconColor: "warning.700",
        textColor: "warning.800",
        label: status === "query" ? "Query raised" : undefined,
      };
    case "not_required":
    case "non_compliant":
    case "not_appropriate":
    case "remove":
      return {
        bg: "error.50",
        borderColor: "error.200",
        borderStyle: "solid",
        icon: XCircle,
        iconColor: "error.700",
        textColor: "error.800",
        label: undefined,
      };
    case "out_of_scope":
    case "removed":
    default:
      return {
        bg: "neutral.75",
        borderColor: "border.default",
        borderStyle: "solid",
        icon: MinusCircle,
        iconColor: "text.body.tertiary",
        textColor: "text.body.secondary",
        label: undefined,
      };
  }
};

export const ReviewChip = memo(
  ({
    status,
    outcomeOptions,
    flaggedCount = 0,
  }: {
    status: string | null | undefined;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
    flaggedCount?: number;
  }) => {
    const style = getReviewChipStyle(status, flaggedCount);

    let label = style.label || "Not reviewed";
    if (status) {
      const outcomeOption = outcomeOptions.find(
        (opt) => opt.standard_mapping === status,
      );
      label = outcomeOption
        ? sentenceCase(outcomeOption.name)
        : style.label || sentenceCase(status);
    }

    return (
      <HStack
        spacing="6px"
        bg={style.bg}
        border="1px solid"
        borderColor={style.borderColor}
        borderStyle={style.borderStyle}
        borderRadius="6px"
        px="8px"
        py="4px"
        display="inline-flex"
        alignItems="center"
      >
        {style.icon && (
          <Icon
            as={style.icon}
            boxSize={3}
            color={style.iconColor}
            flexShrink={0}
          />
        )}
        <Text
          fontSize="12px"
          fontWeight="500"
          color={style.textColor}
          whiteSpace="nowrap"
          lineHeight="1"
        >
          {label}
        </Text>
      </HStack>
    );
  },
);

const StatusIndicator = memo(
  ({
    status,
    outcomeOptions,
  }: {
    status: string | null | undefined;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
  }) => {
    return <ReviewChip status={status} outcomeOptions={outcomeOptions} />;
  },
);

// Decisions whose chosen button reads as a solid fill (design B3): affirmative
// outcomes fill green, out-of-scope-style outcomes fill neutral. All other
// chosen decisions (not_required and peers) read as a tinted, non-solid chip.
export const SOLID_SUCCESS_MAPPINGS = [
  "required",
  "keep",
  "compliant",
  "appropriate",
];
export const SOLID_NEUTRAL_MAPPINGS = ["out_of_scope", "removed"];

/**
 * A person group's decision buttons: one per configured outcome, deciding
 * every still-undecided row in the group at once. No query action — a query
 * is a question about one row.
 */
export const GroupActionButtons = memo(
  ({
    outcomeOptions,
    entitlementOnly = false,
    undecidedCount,
    onDecide,
  }: {
    outcomeOptions: Array<{ name: string; standard_mapping: string }>;
    entitlementOnly?: boolean;
    undecidedCount: number;
    onDecide: (action: string) => void;
  }) => {
    const actions = useMemo(
      () => rowActionSequence(outcomeOptions, entitlementOnly).filter((item) => item.kind === "outcome"),
      [outcomeOptions, entitlementOnly],
    );

    return (
      <HStack spacing={1} onClick={(event) => event.stopPropagation()}>
        {actions.map(({ action }) => {
          const style = getButtonStyle(action, entitlementOnly);
          const name = outcomeOptions.find((o) => o.standard_mapping === action)?.name ?? style.tooltip;
          return (
            <Tooltip
              key={action}
              label={
                undecidedCount === 0
                  ? "Every row for this person is decided"
                  : `${name} for ${undecidedCount} undecided row${undecidedCount === 1 ? "" : "s"}`
              }
            >
              <Button
                size="sm"
                variant="subtle"
                tone={getButtonTone(action)}
                icon={<Icon as={style.icon} boxSize={4} />}
                isDisabled={undecidedCount === 0}
                onClick={() => onDecide(action)}
                aria-label={`${name} for this person`}
              />
            </Tooltip>
          );
        })}
      </HStack>
    );
  },
);

export const getButtonTone = (mapping: string): ButtonTone => {
  if (SOLID_SUCCESS_MAPPINGS.includes(mapping)) return "success";
  if (
    ["not_required", "non_compliant", "not_appropriate", "remove"].includes(
      mapping,
    )
  ) {
    return "error";
  }
  if (["adjust_entitlements", "modify"].includes(mapping)) return "warning";
  if (SOLID_NEUTRAL_MAPPINGS.includes(mapping)) return "secondary";
  return "neutral";
};

const ActionButtons = memo(
  ({
    account,
    onSelect,
    outcomeOptions,
    decisionOnly = false,
    reversibleOutcomes,
    entitlementOnly = false,
  }: {
    account: MFReviewAccount;
    onSelect: (id: string, action: string) => void;
    outcomeOptions: Array<{
      name: string;
      standard_mapping: string;
      requires_note?: boolean;
    }>;
    // Cycle records decisions only — never deprovisions (PLO-5083).
    decisionOnly?: boolean;
    // Locking outcomes this cycle never de-provisions — the actions stay live
    // for a re-decide on one of them (PLO-5749).
    reversibleOutcomes?: readonly string[];
    // Entitlement-only cycle (PLO-5731) — the flag action is dropped and a
    // removal is labelled after the row's one entitlement.
    entitlementOnly?: boolean;
  }) => {
    // Config-driven outcomes (canonical order) + the always-present "Raise a
    // query" action, from the single source of truth in reviewLogic. No
    // hardcoded cap — every configured outcome renders, then query, always.
    // An entitlement-only cycle drops the flag action there: the row is one
    // entitlement, so there is nothing to pick out of it.
    const actions = useMemo(
      () => rowActionSequence(outcomeOptions, entitlementOnly),
      [outcomeOptions, entitlementOnly],
    );
    // not_required (and peers) kicks off de-provisioning the moment it's
    // confirmed — the row locks and ALL actions disable (design B3). A
    // decision-only cycle, or an outcome the cycle configures no action for,
    // never deprovisions, so nothing is irreversible.
    const irreversible = isAccountLocked(
      account,
      decisionOnly,
      reversibleOutcomes,
    );
    const current = account.review;
    const decided = !!current && current !== "query";

    return (
      <HStack
        spacing={1}
        opacity={decided ? 1 : 0.6}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.15s ease"
      >
        {actions.map((item) => {
          // Raise a query — an info action after the outcome icons; asks the
          // campaign admin about this account.
          if (item.kind === "query") {
            return (
              <Tooltip label="Raise a query with the campaign admin" key="query">
                <Button
                  size="sm"
                  variant="subtle"
                  tone="neutral"
                  icon={<Icon as={HelpCircle} boxSize={4} />}
                  isDisabled={irreversible}
                  onClick={() => onSelect(account.id, RAISE_QUERY_ACTION)}
                  aria-label="Raise a query"
                />
              </Tooltip>
            );
          }

          const mapping = item.action;
          const option = outcomeOptions.find(
            (o) => o.standard_mapping === mapping,
          );
          const style = getButtonStyle(mapping, entitlementOnly);
          const isFlag = mapping === "adjust_entitlements";
          const chosen = decided && current === mapping;
          const filledActive =
            chosen && (mapping === "required" || mapping === "out_of_scope");
          // The other decision buttons recede to neutral grey once a decision is
          // made (the flag action keeps its colour), but they stay CLICKABLE:
          // there is no "clear" call, so changing your mind means picking the
          // new outcome directly — the server accepts a fresh decision on an
          // already-decided account (PLO-5749). Only irreversibility locks.
          const recede = decided && !chosen && !isFlag;
          const tone =
            filledActive && mapping === "out_of_scope"
              ? "secondary"
              : recede
                ? "neutral"
                : getButtonTone(mapping);
          const tooltip = irreversible
            ? "De-provisioning started: this can't be changed"
            : recede
              ? `Change to ${option?.name ?? style.tooltip}`
              : style.tooltip;

          return (
            <Tooltip label={tooltip} key={mapping}>
              <Button
                size="sm"
                variant={filledActive ? "solid" : "subtle"}
                tone={tone}
                icon={<Icon as={style.icon} boxSize={4} />}
                isDisabled={irreversible}
                onClick={() => onSelect(account.id, mapping)}
                aria-label={`Mark as ${option?.name ?? mapping}`}
              />
            </Tooltip>
          );
        })}
      </HStack>
    );
  },
);

// The prior outcome reads with the SAME icon vocabulary as the Actions column
// (PLO-4806): green check = kept, red X = removal, orange flag = adjust, grey
// minus = anything else. Keyed off the pure `previousOutcomeIconKind` mapping.
const PREVIOUS_OUTCOME_VISUAL: Record<
  PreviousOutcomeIconKind,
  { icon: typeof CheckCircle; color: string }
> = {
  kept: { icon: CheckCircle, color: "success.700" },
  removal: { icon: XCircle, color: "error.700" },
  adjust: { icon: Flag, color: "warning.700" },
  neutral: { icon: MinusCircle, color: "text.body.tertiary" },
};

// Previous-review cell (PLO-4806): on a re-review, the earlier stage's decision
// for this account — the outcome icon + "Harry Lucas · 16 Jul" beside it. A dash
// for rows with no prior stage (e.g. a stage-1 removal surfaced alongside the
// re-review). The outcome word rides in the whole-cell tooltip; a note (when
// the stage-1 reviewer left one) gets its own speech-bubble icon so its
// existence is visible without hovering the whole cell to discover it.
const PreviousReviewCell = memo(({ account }: { account: MFReviewAccount }) => {
  const prev = previousReviewOf(account);
  if (!prev) {
    return (
      <Text
        color="text.body.tertiary"
        fontFamily="body"
        fontSize="12px"
        fontWeight="500"
        lineHeight="1.2"
      >
        -
      </Text>
    );
  }

  const visual = PREVIOUS_OUTCOME_VISUAL[previousOutcomeIconKind(prev.outcome)];
  const meta = previousReviewMeta(account);
  const tooltip = [prev.stageLabel, previousOutcomeShort(prev.outcome)]
    .filter(Boolean)
    .join(" · ");

  return (
    <HStack spacing="6px" minW={0}>
      <Tooltip label={tooltip} placement="top" hasArrow openDelay={400}>
        <HStack spacing="6px" minW={0}>
          <Icon
            as={visual.icon}
            boxSize="14px"
            color={visual.color}
            flexShrink={0}
          />
          {meta && (
            <Text
              fontFamily="body"
              fontSize="12px"
              fontWeight="500"
              color="text.body.secondary"
              lineHeight="1.2"
              noOfLines={1}
            >
              {meta}
            </Text>
          )}
        </HStack>
      </Tooltip>
      {prev.note && (
        <Tooltip label={prev.note} placement="top" hasArrow openDelay={200}>
          <Box as="span" display="inline-flex" flexShrink={0}>
            <Icon
              as={MessageSquare}
              boxSize="12px"
              color="text.body.tertiary"
              aria-label={`Note: ${prev.note}`}
            />
          </Box>
        </Tooltip>
      )}
    </HStack>
  );
});

// Single entitlement chip component with truncation
// Last-activity cell: emphasises stale access (no activity past the staleness
// threshold) with an amber chip, otherwise shows the last-accessed date.
const LastActivityCell = memo(({ account }: { account: MFReviewAccount }) => {
  const unavailableLabel = activityUnavailableLabel(account);
  if (unavailableLabel) {
    return (
      <Text
        color="text.body.tertiary"
        fontFamily="body"
        fontSize="xs"
        fontWeight="500"
        lineHeight="1.2"
      >
        {unavailableLabel}
      </Text>
    );
  }

  // Prefer the explicit snapshot field, fall back to lastUsageDate for older
  // payloads that only carry the latter. Normalise first so the empty wrapper
  // `{ date: null }` falls through to lastUsageDate instead of shadowing it.
  const source = resolveLastAccessed(
    account.snapshotLastAccessedAt,
    account.lastUsageDate,
  );
  const band = activityBand(source);

  if (band === "stale") {
    return (
      <Tooltip
        label="No recorded activity in the last 90 days"
        placement="top"
        hasArrow
        openDelay={400}
      >
        <HStack
          spacing={1}
          bg="warning.50"
          border="1px solid"
          borderColor="warning.200"
          borderRadius="component.md"
          px="8px"
          py="4px"
          display="inline-flex"
          minH="24px"
        >
          <Icon
            as={AlertTriangle}
            boxSize={3}
            color="warning.700"
            flexShrink={0}
          />
          <Text
            color="warning.800"
            fontFamily="body"
            fontSize="12px"
            fontWeight="600"
            lineHeight="1"
          >
            {activityChipLabel(source)}
          </Text>
        </HStack>
      </Tooltip>
    );
  }

  const iso = source; // already normalised to a string | null ISO date

  if (band === "recent" && iso) {
    return (
      <HStack spacing={1}>
        <Icon as={Clock} boxSize={3} color="text.body.tertiary" />
        <Text
          color="text.body.primary"
          fontFamily="body"
          fontSize="12px"
          fontWeight="500"
          lineHeight="1.2"
        >
          {format(new Date(iso), "MMM d, yyyy")}
        </Text>
      </HStack>
    );
  }

  return (
    <Text
      color="text.body.tertiary"
      fontFamily="body"
      fontSize="12px"
      fontWeight="500"
      lineHeight="1.2"
    >
      No usage data
    </Text>
  );
});

// A single entitlement chip. The type (snapshot category) prefixes the value as
// a styled label — "Repositories: payments-write" — matching the design's
// mode="type" EntitlementTag. Peer context moves into the tooltip (the row chip
// no longer carries a "N/N peers" sub-line). A flagged chip reads as a dashed
// caution outline over a warning tint with a leading flag.
const EntitlementChip = memo(
  ({
    name,
    type,
    description,
    flagged,
    peerLabel,
    maxWidth = "180px",
  }: {
    name: string;
    type?: string | null;
    description?: string | null;
    flagged?: boolean;
    peerLabel?: string | null;
    maxWidth?: string;
  }) => {
    const tooltipLabel = entitlementTooltipLabel({
      name,
      type,
      description,
      peerLabel,
    });
    return (
      <Tooltip
        label={tooltipLabel}
        placement="top"
        hasArrow
        openDelay={500}
        whiteSpace="pre-line"
      >
        <HStack
          bg={flagged ? "bg.warning" : "bg.component"}
          borderRadius="component.md"
          px="8px"
          py="4px"
          spacing="6px"
          display="inline-flex"
          alignItems="center"
          minH="28px"
          maxW={maxWidth}
          overflow="hidden"
          border="1px solid"
          borderStyle={flagged ? "dashed" : "solid"}
          borderColor={flagged ? "warning.400" : "border.default"}
        >
          {flagged && (
            <Icon as={Flag} boxSize={3} color="warning.700" flexShrink={0} />
          )}
          <Text
            fontSize="12px"
            fontWeight="medium"
            color="text.body.primary"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight="1"
          >
            {type && (
              <Text as="span" color="text.label.default" fontWeight={500}>
                {type}:{" "}
              </Text>
            )}
            {name}
          </Text>
        </HStack>
      </Tooltip>
    );
  },
);

// Width-responsive chip row: shows as many chips as fit, with a trailing "+N"
// (plain text) for the rest. A hidden measurement layer holds every chip at full
// width so the fit maths (fitChips) survives resizing either direction. Clicking
// "+N" invokes onOverflowClick (opens the full-list modal). Pure fit logic lives
// in reviewLogic.fitChips; this component only measures + renders.
const OverflowChips = memo(
  ({
    chips,
    onOverflowClick,
  }: {
    chips: { key: string; node: ReactNode }[];
    onOverflowClick?: () => void;
  }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const measRef = useRef<HTMLDivElement>(null);
    const [shown, setShown] = useState(chips.length);

    const keySignature = chips.map((c) => c.key).join("|");
    useLayoutEffect(() => {
      const wrap = wrapRef.current;
      const meas = measRef.current;
      if (!wrap || !meas) return;
      const recompute = () => {
        const widths = (Array.from(meas.children) as HTMLElement[]).map(
          (k) => k.offsetWidth,
        );
        setShown(fitChips(widths, wrap.clientWidth));
      };
      const ro = new ResizeObserver(recompute);
      ro.observe(wrap);
      recompute();
      return () => ro.disconnect();
      // keySignature captures chip identity/count changes.
    }, [keySignature]);

    const hidden = chips.length - shown;
    return (
      <Box ref={wrapRef} position="relative" flex="1" minW={0} overflow="hidden">
        {/* measurement layer — every chip at full width, never painted */}
        <HStack
          ref={measRef}
          spacing="4px"
          position="absolute"
          top={0}
          left={0}
          visibility="hidden"
          pointerEvents="none"
          aria-hidden
        >
          {chips.map((c) => (
            <Box key={c.key} as="span" display="inline-flex">
              {c.node}
            </Box>
          ))}
        </HStack>
        <HStack spacing="4px" minW={0}>
          {chips.slice(0, shown).map((c) => (
            <Box key={c.key} as="span" display="inline-flex" flexShrink={0}>
              {c.node}
            </Box>
          ))}
          {hidden > 0 && (
            <Text
              fontSize="xs"
              color="text.body.tertiary"
              flexShrink={0}
              cursor={onOverflowClick ? "pointer" : "default"}
              _hover={
                onOverflowClick ? { color: "text.body.primary" } : undefined
              }
              onClick={
                onOverflowClick
                  ? (e) => {
                      e.stopPropagation();
                      onOverflowClick();
                    }
                  : undefined
              }
            >
              +{hidden}
            </Text>
          )}
        </HStack>
      </Box>
    );
  },
);

// Entitlements modal for viewing large lists
const EntitlementsModal = memo(
  ({
    isOpen,
    onClose,
    entitlements,
    title = "Entitlements",
  }: {
    isOpen: boolean;
    onClose: () => void;
    entitlements: Array<{
      id: string;
      snapshotEntitlementName: string;
      snapshotEntitlementType?: string | null;
      snapshotEntitlementDescription?: string | null;
      flaggedForAdjustment?: boolean;
    }>;
    title?: string;
  }) => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredEntitlements = useMemo(() => {
      const sorted = [...entitlements].sort((a, b) =>
        a.snapshotEntitlementName.localeCompare(b.snapshotEntitlementName),
      );
      if (!searchQuery.trim()) return sorted;
      const query = searchQuery.toLowerCase();
      return sorted.filter(
        (e) =>
          e.snapshotEntitlementName.toLowerCase().includes(query) ||
          (e.snapshotEntitlementType?.toLowerCase().includes(query) ?? false),
      );
    }, [entitlements, searchQuery]);

    const flaggedCount = useMemo(
      () => entitlements.filter((e) => e.flaggedForAdjustment).length,
      [entitlements],
    );

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader pb={2}>
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="500">
                {title}
              </Text>
              <HStack spacing={3}>
                <Text
                  fontSize="sm"
                  color="text.body.tertiary"
                  fontWeight="normal"
                >
                  {entitlements.length} total
                </Text>
                {flaggedCount > 0 && (
                  <HStack spacing={1}>
                    <Icon as={Flag} boxSize={3} color="warning.700" />
                    <Text fontSize="sm" color="warning.700" fontWeight="normal">
                      {flaggedCount} flagged
                    </Text>
                  </HStack>
                )}
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              {entitlements.length > 10 && (
                <InputGroup size="sm">
                  <InputLeftElement>
                    <Icon as={Search} boxSize={4} color="text.body.tertiary" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search entitlements..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    borderRadius="md"
                  />
                </InputGroup>
              )}
              <VStack
                align="stretch"
                spacing={1}
                maxH="400px"
                overflowY="auto"
                pr={2}
              >
                {filteredEntitlements.length === 0 ? (
                  <Text
                    color="text.body.tertiary"
                    fontSize="sm"
                    textAlign="center"
                    py={4}
                  >
                    No entitlements found
                  </Text>
                ) : (
                  filteredEntitlements.map((e) => (
                    <HStack
                      key={e.id}
                      spacing={2}
                      py={2}
                      px={3}
                      borderRadius="md"
                      bg={e.flaggedForAdjustment ? "warning.50" : "bg.section"}
                      _hover={{
                        bg: e.flaggedForAdjustment
                          ? "warning.100"
                          : "neutral.100",
                      }}
                    >
                      {e.flaggedForAdjustment && (
                        <Icon
                          as={Flag}
                          boxSize={3}
                          color="warning.700"
                          flexShrink={0}
                        />
                      )}
                      <VStack align="start" spacing={0} flex={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          color="text.body.primary"
                        >
                          {e.snapshotEntitlementName}
                        </Text>
                        {e.snapshotEntitlementDescription && (
                          <Text fontSize="xs" color="text.body.secondary">
                            {e.snapshotEntitlementDescription}
                          </Text>
                        )}
                        {e.snapshotEntitlementType && (
                          <Text fontSize="xs" color="text.body.tertiary">
                            {e.snapshotEntitlementType}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                  ))
                )}
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);

// Entitlements cell with chips and modal for large lists
const EntitlementsCell = memo(
  ({ account, onEdit }: { account: MFReviewAccount; onEdit?: () => void }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const entitlements = account.entitlements || [];
    const count = account.entitlementCount ?? entitlements.length;

    if (count === 0) {
      return (
        <HStack spacing={1}>
          <Text
            color="text.body.tertiary"
            fontFamily="body"
            fontSize="12px"
            fontWeight="500"
            lineHeight="1.2"
          >
            -
          </Text>
          {onEdit && <EditEntitlementButton onClick={onEdit} />}
        </HStack>
      );
    }

    // Float flagged entitlements to the front so, when the row is narrow, the
    // chips that survive the overflow cut are the ones that need attention.
    const ordered = [...entitlements].sort(
      (a, b) =>
        (b.flaggedForAdjustment ? 1 : 0) - (a.flaggedForAdjustment ? 1 : 0),
    );

    // A lone chip has no "+N" neighbour to leave room for, so it may use the
    // whole cell instead of the 180px shared-row cap. That cap truncates from
    // the RIGHT, and the type prefix is on the left — on an entitlement-only
    // row ("Mortgages Production: Auditor") it would eat the role name and
    // leave every row of a person reading identically (PLO-5731).
    const chipMaxWidth = ordered.length === 1 ? "100%" : undefined;

    const chips = ordered.map((e) => {
      const parts = entitlementChipParts(e);
      return {
        key: e.id,
        node: (
          <EntitlementChip
            name={parts.value}
            type={parts.type}
            description={e.snapshotEntitlementDescription}
            flagged={e.flaggedForAdjustment}
            peerLabel={peerCompactLabel(e.peerComparison)}
            maxWidth={chipMaxWidth}
          />
        ),
      };
    });

    return (
      <>
        <HStack spacing="6px" minW={0} w="full">
          <OverflowChips chips={chips} onOverflowClick={onOpen} />
          {onEdit && <EditEntitlementButton onClick={onEdit} />}
        </HStack>
        <EntitlementsModal
          isOpen={isOpen}
          onClose={onClose}
          entitlements={entitlements}
          title={`Entitlements for ${account.member?.name || "Account"}`}
        />
      </>
    );
  },
);

// Pencil button for inline entitlement editing
const EditEntitlementButton = memo(({ onClick }: { onClick: () => void }) => (
  <Box
    as="button"
    onClick={(e: any) => {
      e.stopPropagation();
      onClick();
    }}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    w="24px"
    h="24px"
    borderRadius="component.md"
    opacity={0}
    _groupHover={{ opacity: 0.6 }}
    _hover={{ opacity: "1 !important", bg: "neutral.100" }}
    transition="opacity 120ms, background-color 120ms"
    flexShrink={0}
  >
    <Icon as={Pencil} boxSize="12px" color="text.body.tertiary" />
  </Box>
));

// Entitlement type cell - shows only entitlements of a specific type
// displayedTypes is the list of type columns being shown (excluding "Other")
const EntitlementTypeCell = memo(
  ({
    account,
    typeName,
    displayedTypes = [],
    onEdit,
  }: {
    account: MFReviewAccount;
    typeName: string;
    displayedTypes?: string[];
    onEdit?: () => void;
  }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const entitlements = account.entitlements || [];

    // Filter entitlements by type
    // For "Other" column: include entitlements with null type OR types not in displayedTypes
    const typeEntitlements = useMemo(
      () =>
        entitlements.filter((e) => {
          const entType = e.snapshotEntitlementType || null;

          if (typeName === "Other") {
            // "Other" includes: no type, or type not in displayed columns
            return entType === null || !displayedTypes.includes(entType);
          }

          return entType === typeName;
        }),
      [entitlements, typeName, displayedTypes],
    );

    const count = typeEntitlements.length;

    if (count === 0) {
      return (
        <Text
          color="text.body.tertiary"
          fontFamily="body"
          fontSize="12px"
          fontWeight="500"
          lineHeight="1.2"
        >
          -
        </Text>
      );
    }

    // The column header already names the type, so chips here show just the
    // value (design mode="value"). Flagged entitlements still float to the front.
    const ordered = [...typeEntitlements].sort(
      (a, b) =>
        (b.flaggedForAdjustment ? 1 : 0) - (a.flaggedForAdjustment ? 1 : 0),
    );

    const chips = ordered.map((e) => ({
      key: e.id,
      node: (
        <EntitlementChip
          name={e.snapshotEntitlementName}
          description={e.snapshotEntitlementDescription}
          flagged={e.flaggedForAdjustment}
          peerLabel={peerCompactLabel(e.peerComparison)}
        />
      ),
    }));

    return (
      <>
        <HStack spacing="6px" minW={0} w="full">
          <OverflowChips chips={chips} onOverflowClick={onOpen} />
          {onEdit && <EditEntitlementButton onClick={onEdit} />}
        </HStack>
        <EntitlementsModal
          isOpen={isOpen}
          onClose={onClose}
          entitlements={typeEntitlements}
          title={`${typeName} Entitlements for ${account.member?.name || "Account"}`}
        />
      </>
    );
  },
);

// `entitlementOnly` renames the removal outcomes only: in that cycle the
// decision takes away the row's single entitlement rather than the account
// (PLO-5731). Everything else — icon, colour, the other mappings — is unchanged.
export const getButtonStyle = (mapping: string, entitlementOnly = false) => {
  const style = baseButtonStyle(mapping);
  return {
    ...style,
    tooltip: removalActionTooltip(mapping, style.tooltip, entitlementOnly),
  };
};

const baseButtonStyle = (mapping: string) => {
  switch (mapping) {
    case "required":
      return {
        icon: CheckCircle,
        color: "success.700",
        bg: "success.600",
        hoverBg: "success.700",
        tooltip: "Required",
      };

    case "not_required":
      return {
        icon: XCircle,
        color: "error.700",
        bg: "error.600",
        hoverBg: "error.700",
        tooltip: "Not required",
      };

    case "out_of_scope":
      return {
        icon: MinusCircle,
        color: "text.body.tertiary",
        bg: "neutral.500",
        hoverBg: "neutral.600",
        tooltip: "Out of scope",
      };

    case "removed":
      return {
        icon: Trash,
        color: "text.body.tertiary",
        bg: "neutral.500",
        hoverBg: "neutral.600",
        tooltip: "Already Removed",
      };

    case "keep":
      return {
        icon: CheckCircle,
        color: "success.700",
        bg: "success.600",
        hoverBg: "success.700",
        tooltip: "Keep Access",
      };
    case "remove":
      return {
        icon: XCircle,
        color: "error.700",
        bg: "error.600",
        hoverBg: "error.700",
        tooltip: "Remove Access",
      };
    case "modify":
      return {
        icon: AlertTriangle,
        color: "warning.700",
        bg: "warning.600",
        hoverBg: "warning.700",
        tooltip: "Modify Access",
      };
    case "compliant":
      return {
        icon: CheckCircle,
        color: "success.700",
        bg: "success.600",
        hoverBg: "success.700",
        tooltip: "Compliant",
      };
    case "appropriate":
      return {
        icon: CheckCircle,
        color: "success.700",
        bg: "success.600",
        hoverBg: "success.700",
        tooltip: "Appropriate",
      };
    case "non_compliant":
      return {
        icon: XCircle,
        color: "error.700",
        bg: "error.600",
        hoverBg: "error.700",
        tooltip: "Not compliant",
      };
    case "not_appropriate":
      return {
        icon: XCircle,
        color: "error.700",
        bg: "error.600",
        hoverBg: "error.700",
        tooltip: "Not appropriate",
      };
    case "adjust_entitlements":
      return {
        icon: Flag,
        color: "warning.700",
        bg: "warning.600",
        hoverBg: "warning.700",
        tooltip: "Adjust Entitlements",
      };
    default:
      return {
        icon: MinusCircle,
        color: "text.body.tertiary",
        bg: "neutral.500",
        hoverBg: "neutral.600",
        tooltip: mapping.replace(/_/g, " "),
      };
  }
};
export default AccountRow;
